import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Download, Film, AlertCircle, RefreshCw } from 'lucide-react';
import { formatFileSize } from './attachmentUtils';
import { usePresignedFileUrl } from './usePresignedFileUrl';

interface VideoAttachmentProps {
  fileName: string;
  url: string;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
}

export const VideoAttachment: React.FC<VideoAttachmentProps> = ({
  fileName,
  url,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [renderError, setRenderError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const retryCountRef = useRef(0);
  const MAX_AUTO_RETRIES = 2;

  const presigned = usePresignedFileUrl(workspaceId, fileId);

  const displayUrl = presigned.url || url;
  const hasError = presigned.hasError || renderError;

  const backendDownloadUrl = (workspaceId && fileId)
    ? `/api/v1/workspaces/${workspaceId}/files/${fileId}/download?disposition=attachment`
    : displayUrl;

  // Bug 4 fix: Preserve video playback position when presigned URL renews without resetting media element
  useEffect(() => {
    if (videoRef.current && displayUrl && videoRef.current.src !== displayUrl) {
      const currentTime = videoRef.current.currentTime;
      const isPaused = videoRef.current.paused;
      videoRef.current.src = displayUrl;
      if (currentTime > 0) {
        videoRef.current.currentTime = currentTime;
      }
      if (!isPaused) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [displayUrl]);

  const handleVideoError = useCallback(() => {
    if (retryCountRef.current < MAX_AUTO_RETRIES && workspaceId && fileId) {
      retryCountRef.current += 1;
      console.info('[VideoAttachment] Video load error — auto-refreshing presigned URL, attempt', retryCountRef.current);
      setRenderError(false);
      presigned.refresh(true);
    } else {
      setRenderError(true);
    }
  }, [workspaceId, fileId, presigned]);

  const handleRetry = async () => {
    retryCountRef.current = 0;
    setRenderError(false);
    await presigned.refresh(true);
  };

  return (
    <div className="my-2 max-w-lg select-none">
      <div className="rounded-2xl border border-border/80 bg-card overflow-hidden shadow-sm transition hover:shadow-md">
        {/* Video Player */}
        <div className="relative bg-black flex items-center justify-center min-h-[180px] max-h-[380px]">
          {hasError ? (
            <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground p-6 text-center">
              <AlertCircle className="h-8 w-8 text-amber-500" />
              <p className="text-xs font-semibold text-foreground">Video preview unavailable</p>
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Retry</span>
              </button>
            </div>
          ) : (
            <video
              ref={videoRef}
              controls
              preload="metadata"
              onError={handleVideoError}
              className="w-full max-h-[380px] object-contain rounded-t-2xl"
            >
              <source src={displayUrl} />
              Your browser does not support the video tag.
            </video>
          )}
        </div>

        {/* Footer Meta & Download Action */}
        <div className="flex items-center justify-between px-3.5 py-2 bg-card border-t border-border/60 text-xs">
          <div className="flex items-center gap-2.5 min-w-0 pr-2">
            <div className="w-7 h-7 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center flex-shrink-0">
              <Film className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate text-xs">{fileName}</p>
              {(fileSize || presigned.fileSize > 0) && (
                <p className="text-[11px] text-muted-foreground">{formatFileSize(fileSize || presigned.fileSize)}</p>
              )}
            </div>
          </div>

          <a
            href={backendDownloadUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary font-semibold text-xs transition flex-shrink-0"
            title="Download video"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
            <span>Download</span>
          </a>
        </div>
      </div>
    </div>
  );
};
